import React, { useCallback, useEffect, useState } from 'react';
import find from 'lodash/find';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import companyPhoneNumbersResource from 'src/actions/companyPhoneNumber/companyPhoneNumbersResource';
import availableNumbersResource from 'src/actions/availableNumbers/availableNumbersResource';
import { useResourceValues, STATUS_SUCCEEDED } from 'src/presentation/hooks/useResource';
import { AvailablePhoneNumber } from 'src/actions/availableNumbers/availableNumbersResource';
import { CapabilitiesIcons } from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/SearchPhoneNumbers/components';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { H3 } from '@ad-scripts/presentation/components/common/headers';
import { StepWizardFieldWithBorder } from 'src/presentation/components/pages/email/components/StepWizardLayout';
import { PhoneNumberType } from 'src/actions/availableNumbers/availableNumbersResource';
import { StepWizardActionContainer } from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/shared/elements';
import { formatPhone } from 'src/actions/shared/phonesHelper';

const getPhoneTypeTitle = (type: PhoneNumberType) => {
  const phoneTypeMap = {
    [PhoneNumberType.LOCAL]: 'Local',
    [PhoneNumberType.TOLL_FREE]: 'Toll-free',
  };
  return phoneTypeMap[type];
};

const ReviewPhoneNumber = ({
  nextStep,
  previousStep,
  phoneData,
}: {
  nextStep?;
  previousStep?;
  phoneData: AvailablePhoneNumber;
}): JSX.Element | null => {
  const dispatch = useDispatch();
  const { values: availableNumbers } = useResourceValues(availableNumbersResource);
  const { persistingState } = useResourceValues(companyPhoneNumbersResource);
  const { add } = companyPhoneNumbersResource.effects;
  const { phoneNumber, locality, phoneNumberType, capabilities, region } = phoneData;
  const [requestSent, setRequestSent] = useState(false);

  useEffect(() => {
    if (requestSent && persistingState === STATUS_SUCCEEDED) {
      setRequestSent(false);
      nextStep();
    }
  }, [persistingState, nextStep, requestSent, setRequestSent]);

  const onBuyNumber = useCallback(() => {
    const availableNumberData = find(
      availableNumbers,
      (availableNumber: AvailablePhoneNumber) => availableNumber.phoneNumber === phoneNumber
    );
    setRequestSent(true);
    const { accountId, countryCode } = availableNumberData;

    const newPhone = { ...phoneData, accountId, countryCode };

    dispatch(
      add(newPhone, undefined, {
        disableWindowBack: true,
        withoutToast: true,
      })
    );
  }, [add, dispatch, availableNumbers, phoneData, phoneNumber]);

  const isLoading = 'persisting' === persistingState;
  return (
    <>
      <StepWizardFieldWithBorder>
        <H3 data-aid="wizard-title">Review phone number details</H3>
        <StyledTitle>
          <div>{formatPhone(phoneNumber)}</div>
        </StyledTitle>
        {locality ? `${locality}, ` : ''}
        {region ? `${region} ∙ ` : ''}
        {getPhoneTypeTitle(phoneNumberType)}
        <StyledStatuses>
          <CapabilitiesIcons capabilities={capabilities} />
        </StyledStatuses>
        <StyledDescription>
          You&apos;ll be charged now. After, you&apos;ll be <BoldText>charged every month</BoldText> for ownership of
          this number in addition to any usage you incur for this phone number.{' '}
          <ExternalLink
            data-aid="learnMore"
            label="Learn more about telephony billing"
            to="https://help.gladly.com/implementation/docs/invoice-and-billing-overview#telephony-platform-invoices"
          />
        </StyledDescription>
      </StepWizardFieldWithBorder>
      <StepWizardActionContainer>
        <Button data-aid="editor-save" type={Button.Types.PRIMARY} onClick={onBuyNumber} isLoading={isLoading}>
          Buy number
        </Button>
        <Button data-aid="editor-cancel" type={Button.Types.SECONDARY} onClick={previousStep}>
          Back
        </Button>
      </StepWizardActionContainer>
    </>
  );
};

const BoldText = styled.span`
  font-weight: ${p => p.theme.fontWeight.heavy};
`;

const StyledTitle = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  margin-bottom: ${p => p.theme.spacing.insetSmall};
  font-weight: ${p => p.theme.fontWeight.heavy};
`;

const StyledStatuses = styled.div`
  padding-top: ${p => p.theme.spacing.insetSmall};
`;

const StyledDescription = styled.div`
  line-height: 20px;
  margin: ${p => p.theme.spacing.insetMedium} 0 10px 0;
`;

export default ReviewPhoneNumber;
